'use client';
import React, { useEffect, useState } from 'react';
import { Bot, User, ChevronUp, ChevronDown, Zap } from 'lucide-react';

interface OrgEntry {
  id: string;
  rank: number;
  name: string;
  type: 'bot' | 'player';
  size: number;
  score: number;
  absorbed: number;
  color: string;
  zone: string;
  growth: 'up' | 'down' | 'stable';
}

const COLORS = ['#00f5ff','#ff00ff','#39ff14','#ffff00','#ff6600','#bf00ff','#0066ff','#ff0040'];
const ZONES = ['NW-Alpha','NE-Sigma','SW-Delta','SE-Omega','Core-Prime','Mid-Flux','Outer-Rim','Glitch-Zone'];

const MOCK_ORGANISMS: OrgEntry[] = [
  { id: 'org-001', rank: 1, name: 'APEX-NX7', type: 'bot', size: 892, score: 48230, absorbed: 142, color: '#00f5ff', zone: 'Core-Prime', growth: 'up' },
  { id: 'org-002', rank: 2, name: 'VoidPulse', type: 'bot', size: 741, score: 39180, absorbed: 98, color: '#ff00ff', zone: 'NE-Sigma', growth: 'up' },
  { id: 'org-003', rank: 3, name: 'Zyth-Prime', type: 'bot', size: 688, score: 35720, absorbed: 87, color: '#39ff14', zone: 'Mid-Flux', growth: 'stable' },
  { id: 'org-004', rank: 4, name: 'Krell-9', type: 'bot', size: 612, score: 31450, absorbed: 76, color: '#bf00ff', zone: 'SW-Delta', growth: 'up' },
  { id: 'org-005', rank: 5, name: 'OmenFlux', type: 'bot', size: 589, score: 28900, absorbed: 68, color: '#ff6600', zone: 'Outer-Rim', growth: 'down' },
  { id: 'org-006', rank: 6, name: 'Vexor-X', type: 'bot', size: 543, score: 26340, absorbed: 61, color: '#ffff00', zone: 'NW-Alpha', growth: 'up' },
  { id: 'org-007', rank: 7, name: 'NX-Glare', type: 'bot', size: 498, score: 23870, absorbed: 54, color: '#0066ff', zone: 'SE-Omega', growth: 'stable' },
  { id: 'org-008', rank: 8, name: 'PulseCore', type: 'bot', size: 467, score: 21200, absorbed: 49, color: '#ff0040', zone: 'Core-Prime', growth: 'down' },
  { id: 'org-009', rank: 9, name: 'Lyra-88', type: 'bot', size: 441, score: 19880, absorbed: 45, color: '#00f5ff', zone: 'NE-Sigma', growth: 'up' },
  { id: 'org-010', rank: 10, name: 'Dusk-Axon', type: 'bot', size: 418, score: 18650, absorbed: 41, color: '#ff00ff', zone: 'Glitch-Zone', growth: 'up' },
  { id: 'org-011', rank: 11, name: 'Raze-Cyne', type: 'bot', size: 392, score: 17240, absorbed: 38, color: '#39ff14', zone: 'Mid-Flux', growth: 'stable' },
  { id: 'org-012', rank: 12, name: 'Elix-Prime', type: 'bot', size: 371, score: 16100, absorbed: 35, color: '#bf00ff', zone: 'SW-Delta', growth: 'down' },
  { id: 'org-013', rank: 13, name: 'FazeOrbit', type: 'bot', size: 349, score: 14980, absorbed: 32, color: '#ff6600', zone: 'Outer-Rim', growth: 'up' },
  { id: 'org-014', rank: 14, name: 'Grix-Nox', type: 'bot', size: 328, score: 13870, absorbed: 29, color: '#ffff00', zone: 'NW-Alpha', growth: 'stable' },
  { id: 'org-015', rank: 15, name: 'HazeWave', type: 'bot', size: 307, score: 12760, absorbed: 26, color: '#0066ff', zone: 'SE-Omega', growth: 'up' },
  { id: 'org-016', rank: 16, name: 'Izon-9X', type: 'bot', size: 286, score: 11650, absorbed: 23, color: '#ff0040', zone: 'Core-Prime', growth: 'down' },
  { id: 'org-017', rank: 17, name: 'Jett-Vyne', type: 'bot', size: 265, score: 10540, absorbed: 20, color: '#00f5ff', zone: 'NE-Sigma', growth: 'up' },
  { id: 'org-018', rank: 18, name: 'KazeFlux', type: 'bot', size: 244, score: 9430, absorbed: 17, color: '#ff00ff', zone: 'Glitch-Zone', growth: 'stable' },
  { id: 'org-019', rank: 19, name: 'MoxCyber', type: 'bot', size: 223, score: 8320, absorbed: 14, color: '#39ff14', zone: 'Mid-Flux', growth: 'up' },
  { id: 'org-020', rank: 20, name: 'NyxDrift', type: 'bot', size: 202, score: 7210, absorbed: 11, color: '#bf00ff', zone: 'SW-Delta', growth: 'down' },
  { id: 'org-021', rank: 21, name: 'OnyxPrex', type: 'bot', size: 181, score: 6100, absorbed: 8, color: '#ff6600', zone: 'Outer-Rim', growth: 'up' },
  { id: 'org-022', rank: 22, name: 'QuinRyx', type: 'bot', size: 160, score: 4990, absorbed: 5, color: '#ffff00', zone: 'NW-Alpha', growth: 'stable' },
  { id: 'org-023', rank: 23, name: 'RixSyze', type: 'bot', size: 139, score: 3880, absorbed: 3, color: '#0066ff', zone: 'SE-Omega', growth: 'up' },
  { id: 'org-024', rank: 24, name: 'TrixUlyx', type: 'bot', size: 118, score: 2770, absorbed: 1, color: '#ff0040', zone: 'Core-Prime', growth: 'stable' },
  { id: 'org-025', rank: 25, name: 'WexXeno', type: 'bot', size: 97, score: 1660, absorbed: 0, color: '#00f5ff', zone: 'NE-Sigma', growth: 'down' },
];

type SortKey = 'rank' | 'size' | 'score' | 'absorbed';

export default function TopOrganismsTable() {
  const [organisms, setOrganisms] = useState<OrgEntry[]>(MOCK_ORGANISMS);
  const [sortKey, setSortKey] = useState<SortKey>('rank');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [filter, setFilter] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');

  useEffect(() => {
    setLastUpdated(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }));
    // BACKEND INTEGRATION: WebSocket subscription for live organism rankings
    const interval = setInterval(() => {
      setOrganisms(prev => prev.map(o => ({
        ...o,
        size: Math.max(10, o.size + Math.floor((Math.random() - 0.4) * 20)),
        score: o.score + Math.floor(Math.random() * 80),
        growth: Math.random() < 0.4 ? 'up' : Math.random() < 0.6 ? 'stable' : 'down',
      })));
      setLastUpdated(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }));
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sorted = [...organisms]
    .filter(o => o.name.toLowerCase().includes(filter.toLowerCase()) || o.zone.toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      return sortDir === 'asc' ? (av < bv ? -1 : 1) : (av > bv ? -1 : 1);
    });

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return <ChevronUp size={12} className="text-muted-foreground/40" />;
    return sortDir === 'asc' ? <ChevronUp size={12} className="text-primary" /> : <ChevronDown size={12} className="text-primary" />;
  };

  const GrowthIcon = ({ g }: { g: OrgEntry['growth'] }) => {
    if (g === 'up') return <span className="text-neon-green text-xs">↑</span>;
    if (g === 'down') return <span className="text-red-400 text-xs">↓</span>;
    return <span className="text-muted-foreground text-xs">—</span>;
  };

  return (
    <div className="stat-card p-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div>
          <h2 className="text-base font-semibold text-foreground">Top Organisms</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Live arena rankings — all active organisms</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Search name or zone..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="bg-input border border-border rounded px-3 py-1.5 text-xs text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary w-44"
          />
          {lastUpdated && (
            <div className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground">
              <Zap size={10} className="text-neon-green" />
              {lastUpdated}
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/20">
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide w-12">
                <button onClick={() => handleSort('rank')} className="flex items-center gap-1 hover:text-foreground transition-colors">
                  # <SortIcon k="rank" />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <button onClick={() => handleSort('size')} className="flex items-center gap-1 hover:text-foreground transition-colors">
                  Size <SortIcon k="size" />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <button onClick={() => handleSort('score')} className="flex items-center gap-1 hover:text-foreground transition-colors">
                  Score <SortIcon k="score" />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <button onClick={() => handleSort('absorbed')} className="flex items-center gap-1 hover:text-foreground transition-colors">
                  Absorbed <SortIcon k="absorbed" />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Zone</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Growth</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((o, idx) => (
              <tr
                key={o.id}
                className={`border-b border-border/50 transition-colors ${
                  idx % 2 === 0 ? 'bg-card' : 'bg-muted/10'
                } hover:bg-muted/30`}
              >
                <td className="px-4 py-2.5 font-mono text-sm">
                  <span className={`font-bold ${
                    o.rank === 1 ? 'text-yellow-400' : o.rank === 2 ? 'text-gray-300' : o.rank === 3 ? 'text-orange-400' : 'text-muted-foreground'}`}>
                    {o.rank}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: o.color, boxShadow: `0 0 6px ${o.color}` }} />
                    <span className="font-medium text-foreground text-sm">{o.name}</span>
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  {o.type === 'player' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-primary/30 bg-primary/5 text-xs text-primary">
                      <User size={10} /> Player
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-border bg-muted/30 text-xs text-muted-foreground">
                      <Bot size={10} /> AI
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5 font-mono text-sm text-primary">{o.size}</td>
                <td className="px-4 py-2.5 font-mono text-sm text-foreground">{o.score.toLocaleString()}</td>
                <td className="px-4 py-2.5 font-mono text-sm text-neon-magenta">{o.absorbed}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-xs px-2 py-0.5 rounded border font-mono ${
                    o.zone === 'Glitch-Zone' ?'border-neon-green/40 bg-neon-green/5 text-neon-green' :'border-border bg-muted/20 text-muted-foreground'
                  }`}>
                    {o.zone}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <GrowthIcon g={o.growth} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sorted.length === 0 && (
        <div className="py-12 text-center">
          <Bot size={32} className="mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No organisms match your search</p>
        </div>
      )}

      <div className="px-5 py-3 border-t border-border flex items-center justify-between">
        <span className="text-xs text-muted-foreground font-mono">
          Showing {sorted.length} of {organisms.length} organisms
        </span>
        <span className="text-xs text-muted-foreground font-mono">
          Total arena population: ~3,500 AI organisms
        </span>
      </div>
    </div>
  );
}